import type { OrderMoney, PaymentKind, PaymentMethod, PaymentState, PaymentView } from '@fitting-room/contracts'
import { PAYMENT_KINDS, PAYMENT_METHODS, normalizeShopifyOrderNo, signedPaymentMinor } from '@fitting-room/contracts/order-math'
import { BOUTIQUE_LOCALE } from './boutique'
import { formatDay } from './format'
import { formatCents } from './orders'

/**
 * The payment ledger's words and the few decisions the forms make, kept out
 * of the components so they can be tested in `node` like the rest of `lib/`.
 * Ported from tperent's `lib/payments-api.ts`, less rental. The money itself
 * — paid, balance, state — is the API's answer, worked out with
 * `@fitting-room/contracts/order-math`.
 */

export const PAYMENT_KIND_LABEL: Record<PaymentKind, string> = {
  deposit: 'Deposit',
  payment: 'Payment',
  refund: 'Refund',
}

export const PAYMENT_METHOD_LABEL: Record<PaymentMethod, string> = {
  shopify: 'Shopify',
  card: 'Card',
  cash: 'Cash',
  zelle: 'Zelle',
  check: 'Check',
  other: 'Other',
}

/** ClickUp's "In Full / Partial", with the two ends named. */
export const PAYMENT_STATE_LABEL: Record<PaymentState, string> = {
  unpaid: 'Unpaid',
  partial: 'Partial',
  paid: 'Paid in full',
  overpaid: 'Overpaid',
}

export const PAYMENT_KIND_ORDER: readonly PaymentKind[] = PAYMENT_KINDS
export const PAYMENT_METHOD_ORDER: readonly PaymentMethod[] = PAYMENT_METHODS

/** How the money moved, as the end of a sentence: "a deposit of $500.00 by Zelle". */
export const PAYMENT_METHOD_PHRASE: Record<PaymentMethod, string> = {
  shopify: 'through Shopify',
  card: 'by card',
  cash: 'in cash',
  zelle: 'by Zelle',
  check: 'by check',
  other: 'some other way',
}

/** What the reference field is for, by method — the thing to copy off the receipt. */
export const REFERENCE_LABEL: Record<PaymentMethod, string> = {
  shopify: 'Shopify order',
  card: 'Approval code or last 4',
  cash: 'Reference',
  zelle: 'Zelle confirmation',
  check: 'Check number',
  other: 'Reference',
}

/**
 * Why the void button is greyed out. Said on the row when it's pressed — a
 * tablet on the counter has no hover, so a tooltip is a message nobody reads.
 */
export const CANNOT_VOID =
  'Only the person who recorded a payment can void it, and only on the day they recorded it. Ask an owner to void this one.'

/** "$500.00", or "−$200.00" for money that went back out. */
export function signedAmount(payment: Pick<PaymentView, 'kind' | 'amountMinor'>): string {
  const signed = signedPaymentMinor(payment)
  return signed < 0 ? `−${formatCents(-signed)}` : formatCents(signed)
}

/**
 * What the Shopify order number on a payment is called, the way the boutique
 * says it (`docs/22` § "Payments"): the deposit was rung up as the "Initial
 * order", and a payment after a deposit as the "Balance order". Anything else
 * — paid in full in one go, a refund — is just the Shopify order. A voided
 * deposit was never the initial order of anything, so it does not count.
 *
 * `ledger` is the order's payments oldest first, as the ledger lists them.
 */
export function shopifyOrderNoLabel(
  payment: Pick<PaymentView, 'id' | 'kind'>,
  ledger: readonly Pick<PaymentView, 'id' | 'kind' | 'voidedAt'>[],
): string {
  if (payment.kind === 'deposit') return 'Initial order #'
  if (payment.kind === 'payment') {
    const at = ledger.findIndex((p) => p.id === payment.id)
    if (ledger.slice(0, at === -1 ? ledger.length : at).some((p) => p.kind === 'deposit' && !p.voidedAt)) return 'Balance order #'
  }
  return 'Shopify order #'
}

/** A payment by name, for a button among several alike: "the deposit of $500.00 on Oct 14, 2026". */
export function paymentName(payment: Pick<PaymentView, 'kind' | 'amountMinor' | 'paidAt'>): string {
  return `the ${PAYMENT_KIND_LABEL[payment.kind].toLowerCase()} of ${formatCents(payment.amountMinor)} on ${formatDay(payment.paidAt, BOUTIQUE_LOCALE)}`
}

/**
 * What the record-payment dialog starts on. Overpaid, it is a refund of the
 * difference; nothing paid yet, a deposit; otherwise the next payment.
 */
export function defaultPaymentKind(money: OrderMoney): PaymentKind {
  if (money.balanceMinor < 0) return 'refund'
  return money.paidMinor > 0 ? 'payment' : 'deposit'
}

/**
 * The amount the dialog fills in for a kind: what is still owed for money
 * coming in, what was overpaid for a refund, and nothing when there is
 * nothing to suggest — a $0 prefill is a number somebody submits by accident.
 */
export function suggestedAmountMinor(kind: PaymentKind, money: OrderMoney): number | null {
  if (kind === 'refund') return money.balanceMinor < 0 ? -money.balanceMinor : null
  return money.balanceMinor > 0 ? money.balanceMinor : null
}

/** Shopify's financial status, in words: `partially_paid` → "Partially paid". */
export function financialStatusLabel(status: string | undefined): string | null {
  if (!status) return null
  const words = status.replace(/_/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/**
 * The method a payment starts on, on the new order page and in the
 * record-payment dialog alike: Shopify. Nearly all of this boutique's money
 * is rung up there — ClickUp's deposit column is "Dress Deposit (Post Tax -
 * Shopify)" — and a form that started on Card saved Shopify payments as card
 * ones, Shopify order # and all.
 */
export const DEFAULT_PAYMENT_METHOD: PaymentMethod = 'shopify'

/**
 * What the Shopify order # field says when a number is typed under another
 * method: said back, quietly, with the method left alone. Shopify is where a
 * payment starts, so any other method was picked on purpose — a cash deposit
 * rung up in Shopify is still cash (`docs/22` § "Payments"). Nothing to say
 * under Shopify, or with no number.
 */
export function shopifyOrderNoMethodNote(method: PaymentMethod, typed: string): string | undefined {
  if (method === 'shopify' || !normalizeShopifyOrderNo(typed)) return undefined
  return `Recorded ${PAYMENT_METHOD_PHRASE[method]}, with this Shopify order #. If the money went through Shopify, change the method above.`
}
